import React, { useState, useEffect } from 'react';
import ReactGA from "react-ga4";
import { ModelDrawer } from 'landing-page/views/model-drawer';

import { Model } from '@client:common/Model';
import { Theme } from '@client:common/Theme';
import { Backdrop } from '@client:common/overlay/backdrop';

import { ModelCard } from '../model-card/ModelCard';

import './ModelCards.light.scss';
import './ModelCards.dark.scss';

interface Props {
  models: Readonly<Model[]>;
  currentTheme: Theme;
}

const ModelCards = (props: Props) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentModel, setCurrentModel] = useState<Model>();

  const drawerToggleClickHandler = () => {
    setDrawerOpen(!drawerOpen);
  };

  let backdrop;
  if (drawerOpen) {
    backdrop = <Backdrop
      visible={drawerOpen}
      onClick={drawerToggleClickHandler} />;
  }

  useEffect(() => {
    if (currentModel && drawerOpen) {
      ReactGA.event({
        category: "Model Card",
        action: `opened_model_card_from_dashboard_${currentModel.acronym}`,
        label: `Opened model card from dashboard. Model: ${currentModel.acronym}`
      })
      ReactGA.event({
        category: "Model Card",
        action: `opened_model_card_from_dashboard`,
        label: `Opened model card from dashboard. Count across all model card openings irrespective of the specific model`
      })
    }
  }, [currentModel, drawerOpen])

  return (
    <>
      {backdrop}
      {currentModel && (
        <ModelDrawer
          currentTheme={props.currentTheme}
          show={drawerOpen}
          toggle={drawerToggleClickHandler}
          model={currentModel}
        />
      )}
      <div className='model-cards-container'>
        {props.models.map((model) => (
          <div
            key={model.id}
            className={`model-card ${model.id}`}
            onClick={() => {
              drawerToggleClickHandler();
              setCurrentModel(model);
            }}
          >
            <ModelCard
              key={model.name}
              model={model}
              currentTheme={props.currentTheme} />
          </div>
        ))}
      </div>

      <footer className='landing-page__footer'>
        <h2>Our Story and Purpose</h2>
        <p>
          Energy storage systems (ESS) can be used for a variety of grid and end-user applications and hold great
          potential for a decarbonized grid with high-renewable penetration. Numerous used cases and valuation tools
          have been developed during the past few years to help various stakeholders identify value streams and evaluate
          the economic benefits of ESS, as reported in{' '}
          <a
            href='https://www.energy.gov/sites/default/files/2022-06/MSP_Report_2022June_Final_508_v3.pdf'
            target='_blank'
            rel='noreferrer'
          >
            Energy Storage Valuation: A Review of Use Cases and Modeling Tools
          </a>
          . There exist numerous similarities and differences among these tools. It is not easy for many users to
          differentiate these tools and select the most appropriate tools to meet their specific needs. To address this
          challenge, a model selection platform (MSP) has been developed at Pacific Northwest National Laboratory to
          review and compare a list of energy storage tools developed by the U.S. Department of Energy national
          laboratories and suggest the best-suited tools based on users’ needs and requirements.
        </p>
        <div className='landing-page__footer-logo-container'>
          <figure className='landing-page__footer-logo'>
            <img src={`/images/${props.currentTheme}-mode-pnnl-logo.png`} />
          </figure>
          <figure className='landing-page__footer-logo'>
            <img src={`/images/${props.currentTheme}-mode-doe-logo.png`} />
          </figure>
          <figure className='landing-page__footer-logo'>
            <img src={`/images/${props.currentTheme}-mode-energy-storage-grand-challenge-logo.png`} />
          </figure>
        </div>
      </footer>
    </>
  );
};

export default ModelCards;
